import { useMemo, useState } from "react";
import { SearchNormal } from "iconsax-reactjs";
import { X } from "lucide-react";
import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../../shared/ui";

type AssignableBusiness = {
  id: string;
  name: string;
  email?: string;
  user?: {
    email?: string;
  };
};

type AssignAccountManagerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberName: string;
  allBusinesses: AssignableBusiness[];
  selectedBusinessIds: string[];
  onToggleBusiness: (businessId: string) => void;
  onCancel: () => void;
  onAssign: () => void;
  isAssigning?: boolean;
  error?: string | null;
};

const AssignAccountManagerModal = ({
  open,
  onOpenChange,
  memberName,
  allBusinesses,
  selectedBusinessIds,
  onToggleBusiness,
  onCancel,
  onAssign,
  isAssigning = false,
  error,
}: AssignAccountManagerModalProps) => {
  const [query, setQuery] = useState("");

  const filteredBusinesses = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return allBusinesses;
    return allBusinesses.filter((business) => {
      const nameMatch = business.name.toLowerCase().includes(normalized);
      const emailMatch = (business.email ?? "")
        .toLowerCase()
        .includes(normalized);
      return nameMatch || emailMatch;
    });
  }, [allBusinesses, query]);

  const groupedBusinesses = useMemo(() => {
    return filteredBusinesses.reduce<Record<string, AssignableBusiness[]>>(
      (acc, business) => {
        const key = business.name.charAt(0).toUpperCase() || "#";
        if (!acc[key]) acc[key] = [];
        acc[key].push(business);
        return acc;
      },
      {},
    );
  }, [filteredBusinesses]);

  const orderedGroupKeys = useMemo(
    () => Object.keys(groupedBusinesses).sort((a, b) => a.localeCompare(b)),
    [groupedBusinesses],
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      contentClassName="max-w-[600px]"
    >
      <ModalHeader>
        <div>
          <div className="mb-2 flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-full bg-[#EEF1FB] text-[14px] font-[600] text-[#3A3E5B]">
              {(memberName.charAt(0) || "M").toUpperCase()}
            </div>
            <span className="text-[16px] font-[500] text-[#2C2F44]">
              {memberName}
            </span>
          </div>
          <ModalTitle>Assign as account manager</ModalTitle>
          <p className="mt-2 text-[16px] text-[#6E7397]">
            Select customers you want assigned to this admin.
          </p>
        </div>
        <button
          type="button"
          aria-label="Close modal"
          className="rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF]"
          onClick={onCancel}
          disabled={isAssigning}
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <ModalBody className="pt-4">
        <div className="space-y-4">
          <div className="flex h-12 items-center gap-2 rounded-[12px] bg-[#E9ECFA] px-3">
            <SearchNormal size={18} className="text-[#737AA8]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by business name or email"
              className="w-full bg-transparent text-[14px] text-[#2C2F44] placeholder:text-[#737AA8] focus:outline-none"
            />
          </div>

          <div className="max-h-[56vh] space-y-4 overflow-y-auto pr-1">
            {orderedGroupKeys.length === 0 ? (
              <div className="rounded-[12px] px-3 py-4 text-[14px] text-[#6B6F8A]">
                No customers found.
              </div>
            ) : (
              orderedGroupKeys.map((groupKey) => (
                <div key={groupKey}>
                  <div className="mb-2 text-[16px] font-[500] text-[#6E7397]">
                    {groupKey}
                  </div>
                  <div className="space-y-2 rounded-[16px] bg-white p-3">
                    {groupedBusinesses[groupKey].map((business) => {
                      const isSelected = selectedBusinessIds.includes(
                        business.id,
                      );
                      const initials = business.name
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((part) => part.charAt(0).toUpperCase())
                        .join("");

                      return (
                        <button
                          key={business.id}
                          type="button"
                          onClick={() => onToggleBusiness(business.id)}
                          className="flex w-full items-center gap-3 rounded-[12px] px-2 py-2 text-left hover:bg-[#F7F8FD]"
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={() => onToggleBusiness(business.id)}
                            className="shrink-0"
                            size="sm"
                          />
                          <div className="grid h-9 w-9 place-items-center rounded-full bg-[#EEF1FB] text-[14px] font-[600] text-[#3A3E5B]">
                            {initials || business.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-[16px] font-[500] text-[#161827]">
                              {business.name}
                            </div>
                            <div className="truncate text-[14px] text-[#6E7397]">
                              {business.email ??
                                business.user?.email ??
                                "No email"}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          variant="ghost"
          className="h-11 rounded-[14px] px-6 text-[#2C2F44]"
          onClick={onCancel}
          disabled={isAssigning}
        >
          Cancel
        </Button>
        <Button
          className="h-11 rounded-[14px] px-6"
          onClick={onAssign}
          disabled={selectedBusinessIds.length === 0 || isAssigning}
        >
          {isAssigning ? "Assigning..." : "Assign customers"}
        </Button>
      </ModalFooter>
      {error ? (
        <div className="px-6 pb-6 text-[14px] text-[#C24E4E]">{error}</div>
      ) : null}
    </Modal>
  );
};

export type { AssignableBusiness };
export default AssignAccountManagerModal;
